/**
 * SearchBar Component
 * Modern search interface with URL detection, barcode scanner, and autocomplete
 * Feature flags controlled via Firebase Remote Config
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { functions, httpsCallable, getRemoteConfigValue } from '@/lib/firebase.client';

interface SearchBarProps {
  initialQuery?: string;
  initialMode?: 'FSA' | 'HSA';
  autoFocus?: boolean;
}

export default function SearchBar({ initialQuery = '', initialMode = 'FSA', autoFocus = false }: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [mode, setMode] = useState<'FSA' | 'HSA'>(initialMode);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isScanning, setIsScanning] = useState(false);
  const [featureBarcode, setFeatureBarcode] = useState(true);
  const [featureUrlDetection, setFeatureUrlDetection] = useState(true);
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  // Load feature flags from Remote Config
  useEffect(() => {
    try {
      const barcodeEnabled = getRemoteConfigValue('feature_barcode_scanner');
      const urlDetectionEnabled = getRemoteConfigValue('feature_url_detection');
      setFeatureBarcode(barcodeEnabled !== 'false');
      setFeatureUrlDetection(urlDetectionEnabled !== 'false');
    } catch (error) {
      console.error('Error loading feature flags:', error);
    }
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Fetch autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const autocompleteCallable = httpsCallable(functions, 'autocomplete');
        const response = await autocompleteCallable({ q: query });
        const data = response.data as { suggestions: string[] };
        setSuggestions(data.suggestions || []);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  // Detect if query is a URL
  const isURL = (str: string) => {
    try {
      new URL(str);
      return true;
    } catch {
      return /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(str);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      // Only use URL detection if feature is enabled
      const searchQuery = (featureUrlDetection && isURL(query)) ? `url:${query.trim()}` : query.trim();
      router.push(`/results?q=${encodeURIComponent(searchQuery)}&mode=${mode}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    router.push(`/results?q=${encodeURIComponent(suggestion)}&mode=${mode}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBarcodeScan = async () => {
    setIsScanning(true);
    try {
      // Check if browser supports BarcodeDetector
      if ('BarcodeDetector' in window) {
        // Use native barcode detection if available
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        // TODO: Implement barcode scanning UI
        alert('Barcode scanning will be implemented here. For now, please enter the barcode manually.');
        stream.getTracks().forEach(track => track.stop());
      } else {
        // Fallback: prompt for manual entry
        const barcode = prompt('Enter barcode number:');
        if (barcode) {
          const barcodeLookupCallable = httpsCallable(functions, 'barcodeLookup');
          const response = await barcodeLookupCallable({ barcode });
          const data = response.data as { productName?: string };
          if (data.productName) {
            setQuery(data.productName);
            router.push(`/results?q=${encodeURIComponent(data.productName)}&mode=${mode}`);
          } else {
            alert('Product not found for this barcode.');
          }
        }
      }
    } catch (error) {
      console.error('Barcode scan error:', error);
      alert('Unable to access camera or lookup barcode. Please enter the barcode manually.');
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="relative">
        {/* Modern Search Container */}
        <div className="flex flex-col md:flex-row items-stretch bg-white rounded-3xl shadow-2xl border border-gray-100 hover:shadow-3xl transition-all duration-300 overflow-hidden">
          {/* Main Search Area */}
          <div className="flex items-center flex-1 relative">
            {/* Search Icon */}
            <div className="absolute left-6 text-gray-400">
              <svg
                className="w-5 h-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Search Input */}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(true);
                setSelectedIndex(-1);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={handleKeyDown}
              placeholder="Search item, paste URL, or scan barcode..."
              className="flex-1 pl-14 pr-24 md:pr-28 py-5 text-base md:text-lg outline-none text-gray-900 placeholder-gray-400"
              aria-label="Search for FSA or HSA eligible items"
            />

            {/* Right side actions container */}
            <div className="absolute right-3 md:right-4 flex items-center gap-2">
              {/* Clear Button - only visible when there's text */}
              {query.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setSuggestions([]);
                    setShowSuggestions(false);
                    inputRef.current?.focus();
                  }}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* URL Indicator - only show if URL detection is enabled */}
              {featureUrlDetection && isURL(query) && query.length > 0 && (
                <div className="hidden sm:flex items-center gap-1 md:gap-2 text-xs font-semibold text-primary-600 bg-primary-50 px-2 md:px-3 py-1 rounded-full">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" />
                  </svg>
                  <span className="hidden md:inline">URL Detected</span>
                  <span className="md:hidden">URL</span>
                </div>
              )}
            </div>
          </div>

          {/* Mode Dropdown */}
          <div className="relative border-t md:border-t-0 md:border-l border-gray-100">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as 'FSA' | 'HSA')}
              className="appearance-none w-full md:w-32 px-6 py-5 pr-10 font-bold text-sm bg-white text-gray-800 hover:bg-gray-50 transition-all duration-200 outline-none cursor-pointer"
              aria-label="Select account type"
            >
              <option value="FSA">FSA</option>
              <option value="HSA">HSA</option>
            </select>
            {/* Dropdown Arrow */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            className="hidden md:flex items-center justify-center px-8 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold hover:from-primary-600 hover:to-primary-700 transition-all duration-200"
            aria-label="Search"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>

        {/* Mobile Search Button */}
        <button
          type="submit"
          className="mt-3 w-full md:hidden bg-gradient-to-r from-primary-500 to-primary-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-lg"
        >
          Search
        </button>
      </form>

      {/* Quick Actions */}
      <div className="mt-4 flex flex-wrap gap-3 justify-center">
        {/* Barcode Scanner - only show if feature is enabled */}
        {featureBarcode && (
          <button
            type="button"
            onClick={handleBarcodeScan}
            disabled={isScanning}
            className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-full text-sm font-semibold text-gray-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            {isScanning ? 'Scanning...' : 'Scan Barcode'}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            const randomItems = ['Sunscreen', 'Contact Lenses', 'First Aid Kit', 'Blood Pressure Monitor', 'Thermometer', 'Bandages'];
            const random = randomItems[Math.floor(Math.random() * randomItems.length)];
            setQuery(random);
            router.push(`/results?q=${encodeURIComponent(random)}&mode=${mode}`);
          }}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-full text-sm font-semibold text-gray-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          Surprise Me
        </button>
      </div>

      {/* Autocomplete Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-left px-6 py-4 hover:bg-primary-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                index === selectedIndex ? 'bg-primary-50' : ''
              }`}
            >
              <div className="flex items-center space-x-3">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-gray-800 font-medium">{suggestion}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
